import React, { createContext, useContext, useRef, useState, useCallback, type ReactNode } from 'react';

type RefreshCallback = () => Promise<void>;

interface SidebarRefreshContextType {
  refreshCallback: React.MutableRefObject<RefreshCallback | null>;
  refreshTrigger: number;
  setRefreshTrigger: (value: number) => void;
}

const SidebarRefreshContext = createContext<SidebarRefreshContextType | undefined>(undefined);

export function SidebarRefreshProvider({ children }: { children: ReactNode }) {
  const refreshCallback = useRef<RefreshCallback | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  return (
    <SidebarRefreshContext.Provider value={{ refreshCallback, refreshTrigger, setRefreshTrigger }}>
      {children}
    </SidebarRefreshContext.Provider>
  );
}

export function useSidebarRefreshRegister() {
  const context = useContext(SidebarRefreshContext);
  if (context === undefined) {
    throw new Error('useSidebarRefreshRegister must be used within SidebarRefreshProvider');
  }

  return {
    setRefreshCallback: (callback: RefreshCallback) => {
      context.refreshCallback.current = callback;
    },
  };
}

export function useSidebarRefreshTrigger() {
  const context = useContext(SidebarRefreshContext);
  if (context === undefined) {
    throw new Error('useSidebarRefreshTrigger must be used within SidebarRefreshProvider');
  }

  const triggerSidebarRefresh = useCallback(async () => {
    if (context.refreshCallback.current) {
      await context.refreshCallback.current();
    } else {
      console.warn('[useSidebarRefreshTrigger] No refresh callback registered');
    }
    // Emit refresh signal to other components watching for refresh
    context.setRefreshTrigger(prev => prev + 1);
  }, [context]);

  return {
    triggerSidebarRefresh,
    refreshTrigger: context.refreshTrigger,
  };
}
